/* eslint-disable no-restricted-syntax */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Stack } from '@mui/material';
import { Star, Shield } from '@mui/icons-material';
import FamilyPill from './FamilyPill';
import useThemeStyles from '../../hooks/useThemeStyles';

const CharacterCard = ({ character, family, onClick, onFamilyClick }) => {
  const { colors } = useThemeStyles();

  // Extract role from tags
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const roleTags = ['Protagonist', 'Supporting', 'Minor', 'Antagonist', 'Deuteragonist'];
  const characterRole = useMemo(() => {
    if (!character.tags || !Array.isArray(character.tags)) return null;
    for (const tag of character.tags) {
      const roleMatch = roleTags.find(
        (role) =>
          tag.toLowerCase().includes(role.toLowerCase()) ||
          role.toLowerCase().includes(tag.toLowerCase())
      );
      if (roleMatch) return roleMatch;
    }
    return null;
  }, [character.tags, roleTags]);

  // Extract gender from tags or use a default
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const genderTags = ['Male', 'Female', 'Non-binary', 'Other'];
  const characterGender = useMemo(() => {
    if (!character.tags || !Array.isArray(character.tags)) return null;
    for (const tag of character.tags) {
      const genderMatch = genderTags.find(
        (gender) =>
          tag.toLowerCase().includes(gender.toLowerCase()) ||
          gender.toLowerCase().includes(tag.toLowerCase())
      );
      if (genderMatch) return genderMatch;
    }
    return null;
  }, [character.tags, genderTags]);

  const cardStyle = {
    padding: '16px',
    marginBottom: '12px',
    backgroundColor: colors.paper.background,
    borderRadius: '8px',
    border: `1px solid ${colors.border.subtle}`,
    cursor: onClick ? 'pointer' : 'default',
    transition: 'all 0.2s ease',
    '&:hover': onClick
      ? {
          backgroundColor: colors.background.surface,
          boxShadow: `0 2px 8px ${colors.paper.shadow}`,
          transform: 'translateY(-2px)',
        }
      : {},
  };

  return (
    <Box sx={cardStyle} onClick={onClick}>
      <Stack spacing={1.5}>
        {/* Name and Family Tag */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: colors.text.primary,
              fontSize: '1.1rem',
            }}
          >
            {character.displayName || 'Unnamed Character'}
          </Typography>
          {family && <FamilyPill family={family} onClick={onFamilyClick} />}
        </Box>

        {/* Age, Gender, Role */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {character.age !== null && character.age !== undefined && (
            <Typography component="span" sx={{ color: colors.text.primary, fontSize: '0.875rem' }}>
              Age: {character.age}
            </Typography>
          )}
          {characterGender && (
            <Typography component="span" sx={{ color: colors.text.primary, fontSize: '0.875rem' }}>
              Gender: {characterGender}
            </Typography>
          )}
          {characterRole && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {characterRole === 'Protagonist' && (
                <Star sx={{ fontSize: '16px', color: '#FFD700' }} />
              )}
              {(characterRole === 'Supporting' || characterRole === 'Deuteragonist') && (
                <Shield sx={{ fontSize: '16px', color: '#FFD700' }} />
              )}
              <Typography
                component="span"
                sx={{ color: colors.text.primary, fontSize: '0.875rem' }}
              >
                Role: {characterRole}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Description */}
        {character.personality && (
          <Typography
            sx={{
              color: colors.text.secondary,
              fontSize: '0.875rem',
              lineHeight: 1.5,
            }}
          >
            {character.personality}
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

CharacterCard.propTypes = {
  character: PropTypes.shape({
    id: PropTypes.string.isRequired,
    displayName: PropTypes.string,
    fullName: PropTypes.string,
    age: PropTypes.number,
    race: PropTypes.string,
    hairColor: PropTypes.string,
    eyeColor: PropTypes.string,
    sexuality: PropTypes.string,
    personality: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.string),
    familyId: PropTypes.string,
  }).isRequired,
  family: PropTypes.shape({
    id: PropTypes.string,
    familyName: PropTypes.string,
  }),
  onClick: PropTypes.func,
  onFamilyClick: PropTypes.func,
};

CharacterCard.defaultProps = {
  family: null,
  onClick: null,
  onFamilyClick: null,
};

export default CharacterCard;
